import React from 'react';
import { Box, Typography, Grid, useTheme } from '@mui/material';
import { useBrand } from './ServerBrandProvider';
import { useTenantTerminology } from '../hooks/useTenantTerminology';

interface PageTitleProps {
  title: string;
  titleKey?: string; // Translation key for tenant-specific terminology
  subtitle?: string;
  subtitleKey?: string; // Translation key for subtitle
  icon?: React.ReactElement;
  middleContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  children?: React.ReactNode;
  variant?: 'default' | 'compact';
}

export const PageTitle: React.FC<PageTitleProps> = ({
  title,
  titleKey,
  subtitle,
  subtitleKey,
  icon,
  middleContent,
  rightContent,
  children,
  variant = 'default',
}) => {
  const { brand } = useBrand();
  const theme = useTheme();
  const { t } = useTenantTerminology();

  // Use tenant-specific terminology if key provided, otherwise use title as-is
  const displayTitle = titleKey ? t(titleKey, title) : title;
  const displaySubtitle = subtitleKey ? t(subtitleKey, subtitle || '') : subtitle;

  // Use brand colors or fallback to theme colors
  const primaryColor = brand?.colors?.primary || theme.palette.primary.main;
  const secondaryColor = brand?.colors?.secondary || theme.palette.secondary.main;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        mb: 3,
      }}
    >
      <Box
        sx={{
          background:
            brand?.theme?.headerGradient ||
            `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          p: 3,
          color: 'white',
          borderRadius: '12px 12px 0 0',
          mx: 0,
          mt: 0,
          width: '100%',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            {icon && React.cloneElement(icon, { sx: { fontSize: 48, color: 'white' } })}
          </Grid>
          <Grid item xs={middleContent ? undefined : true}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
              {displayTitle}
            </Typography>
            {displaySubtitle && (
              <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                {displaySubtitle}
              </Typography>
            )}
          </Grid>
          {middleContent && (
            <Grid item xs>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {middleContent}
              </Box>
            </Grid>
          )}
          {rightContent && (
            <Grid item>
              {rightContent}
            </Grid>
          )}
        </Grid>
        {children && (
          <Box sx={{ mt: 2 }}>
            {children}
          </Box>
        )}
      </Box>
    </Box>
  );
};